import React, { useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { clearPluginsCache } from "../ChannelIcon";

const useStyles = makeStyles((theme) => ({
  explicacao: {
    marginBottom: theme.spacing(2),
  },
  bloco: {
    background: theme.palette.type === "dark" ? "#1e1e1e" : "#f5f5f5",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1.5),
    fontFamily: "monospace",
    fontSize: 12,
    // O token é longo e não tem espaços: sem isto ele estoura a caixa em vez
    // de quebrar linha.
    wordBreak: "break-all",
    whiteSpace: "pre-wrap",
    cursor: "pointer",
    marginBottom: theme.spacing(1),
  },
  rotulo: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
  },
  aviso: {
    marginTop: theme.spacing(2),
  },
}));

/**
 * Conectar um plugin que roda em qualquer lugar, sem porta aberta.
 *
 * O caminho tradicional ("Adicionar Plugin Remoto") pede a URL do plugin,
 * porque é o backend que disca nele — o que exige porta publicada, DNS e TLS.
 * Aqui é o contrário: o Markedesk gera um token, o operador cola no plugin, e
 * o plugin liga de volta. Serve para plugin atrás de NAT, em container sem
 * porta exposta, ou na máquina de um terceiro.
 *
 * O token carrega a URL do backend e a credencial numa string só — separá-las
 * é o que faz alguém parear o endereço de um ambiente com a chave de outro.
 */
const PluginInstallToken = ({ open, onClose }) => {
  const classes = useStyles();
  const [token, setToken] = useState("");
  const [reaproveitada, setReaproveitada] = useState(false);
  const [loading, setLoading] = useState(false);

  const gerar = async () => {
    setLoading(true);
    try {
      // Sem pluginId: quem sabe qual plugin é, é o plugin — ele manda o
      // metadata no handshake e o backend batiza a instalação ali. Exigir aqui
      // obrigaria a decorar o identificador de algo que ainda nem subiu.
      const { data } = await api.post("/plugins/install-token", {});
      setToken(data.token);
      setReaproveitada(!!data.reaproveitada);
      clearPluginsCache();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const copiar = (texto, oQue) => {
    navigator.clipboard.writeText(texto);
    toast.info(`${oQue} copiado!`);
  };

  const fechar = () => {
    setToken("");
    setReaproveitada(false);
    onClose();
  };

  // A imagem fica como marcador: quem gera o convite ainda não sabe qual plugin
  // vai atendê-lo — e esse é justamente o ponto deste fluxo.
  const comandoDocker = token
    ? `docker run -d \\\n  -e MARKEDESK_TOKEN=${token} \\\n  <imagem-do-plugin>`
    : "";

  return (
    <Dialog open={open} onClose={fechar} maxWidth="sm" fullWidth>
      <DialogTitle>Conectar plugin sem porta</DialogTitle>
      <DialogContent>
        {token ? (
          <>
            <Typography variant="body2" className={classes.explicacao}>
              {reaproveitada
                ? "Esta instalação já existia — este é o mesmo token de antes, não um novo."
                : "Instalação criada. O plugin aparecerá na lista assim que conectar."}
            </Typography>

            <Typography variant="body2" className={classes.rotulo}>
              Token de instalação
            </Typography>
            <div
              className={classes.bloco}
              onClick={() => copiar(token, "Token")}
              title="Clique para copiar"
            >
              {token}
            </div>

            <Typography variant="body2" className={classes.rotulo}>
              Ou rode direto
            </Typography>
            <div
              className={classes.bloco}
              onClick={() => copiar(comandoDocker, "Comando")}
              title="Clique para copiar"
            >
              {comandoDocker}
            </div>

            <Typography
              variant="caption"
              color="textSecondary"
              component="div"
              className={classes.aviso}
            >
              O token vale como senha: quem o tiver conecta um plugin nesta
              empresa. Não compartilhe em canal aberto. Para revogar, remova o
              plugin da lista.
            </Typography>
          </>
        ) : (
          <Typography variant="body2" className={classes.explicacao}>
            O plugin não precisa de porta aberta, domínio nem certificado — é
            ele que liga para o Markedesk. Gere o token, leve até o plugin, e
            ele aparece aqui sozinho ao conectar: quem diz qual plugin é, é o
            próprio plugin.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={fechar} color="default">
          {token ? "Fechar" : "Cancelar"}
        </Button>
        {!token && (
          <Button
            onClick={gerar}
            color="primary"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            Gerar token
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PluginInstallToken;
